import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDrag } from 'react-dnd';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Avatar, Box, Button, Card, IconButton, Popover, TextField, Tooltip, Typography } from '@mui/material';
import { CalendarToday, PauseCircleOutlined, PlayCircleOutline } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { toast } from 'react-toastify';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useStyles } from '../styles/taskCardStyles';
import { stringAvatar } from '../../../../utils/avatarCreator';
import getEndDateInfo from '../../../../utils/getEndDateInfo';
import { trackerRunningService } from '../../../../services/trackerService';
import { updateTask } from '../../../../services/taskService';
import { useProject } from '../../Layout/header/context';
import { isAdmin, isClient, isStakeholder } from '../../../../utils/share';

const TaskCard = ({ task, setLoading, runningTask, refetchSection, ...props }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'task',
    item: task,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));
  const isCompleted = task?.status === 'Completed';

  const classes = useStyles({ isDragging, isCompleted });

  const userId = useSelector((state) => state?.login?.user?.id);
  const { projectRole } = useProject();

  const [endDate, setEndDate] = useState(dayjs(task?.endDate));
  const [anchorEl, setAnchorEl] = useState(null);

  const isNotEditable = projectRole === 'Team Members' && task?.userId !== userId && !isAdmin();

  const handleDateChange = async (newDate) => {
    try {
      if (isNotEditable) return;

      await updateTask(task?.id, { endDate: newDate });
      setEndDate(newDate);
      // setTaskList((prev) => prev.map((t) => (t.id === task?.id ? { ...t, endDate: newDate } : t)));
      refetchSection();
      // toast.info('End date updated successfully');
      toast.success('Deadline updated successfully');
    } catch (error) {
      console.error('Failed to update the end date', error);
      toast.error('Failed to update the deadline. Please try again later.');
    }
    setAnchorEl(null);
  };

  const handleEndDateBtnClick = (event) => {
    event.stopPropagation();
    if (isNotEditable) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleToggleTaskStatus = async (e) => {
    e.stopPropagation();
    if (isNotEditable) return;

    try {
      setLoading(true);
      const status = isCompleted ? 'In Progress' : 'Completed';
      await updateTask(task?.id, { status });
      refetchSection();
      toast.info('Task status updated');
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the task status. Please try again later.';
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTimer = async (e) => {
    e.stopPropagation();
    if (task?.userId !== userId) return;

    try {
      setLoading(true);
      const startTime = new Date().toISOString();
      const taskId = task?.id;
      const projectId = task?.projectId;
      await trackerRunningService({ startTime, taskId, projectId });
      refetchSection();
      toast.info('Timer Started');
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while starting the timer. Please try again later.';
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleStopTimer = async (e) => {
    e.stopPropagation();
    if (task?.userId !== userId) return;

    try {
      setLoading(true);
      const stopTime = new Date().toISOString();
      const taskId = task?.id;
      const projectId = task?.projectId;
      await trackerRunningService({ stopTime, taskId, projectId });
      refetchSection();
      toast.info('Timer Paused');
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while pausing the timer. Please try again later.';
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const userName = task?.user?.userName ?? '';
  const { endDateText, endDateColor } = getEndDateInfo(task?.endDate);

  return (
    <Card className={classes?.card} disabled={isAdmin()} classes={{ root: classes?.root }} {...props} ref={drag}>
      <Box className={classes?.taskContainer}>
        <Typography component="span" className={classes?.taskName}>
          <Tooltip title={isCompleted ? 'Mark task in-progress' : 'Mark task complete'} placement="top" arrow>
            <IconButton className={classes.solidTick} onClick={handleToggleTaskStatus} disabled={isNotEditable}>
              {isCompleted ? (
                <CheckCircleSolid className={classes.solidTick} />
              ) : (
                <CheckCircleOutline className={classes.outlinedTick} />
              )}
            </IconButton>
          </Tooltip>

          {task?.taskName}
          {task?.priority && (
            <Box
              component="span"
              sx={{
                ml: 1,
                px: '6px',
                py: '2px',
                fontSize: '11px',
                borderRadius: '6px',
                background: '#eee',
                fontWeight: 600,
              }}
            >
              {task.priority}
            </Box>
          )}
        </Typography>
      </Box>
      {task?.attachments?.length !== 0 && (
        <Tooltip title="Attachments">
          <AttachFileIcon
            fontSize="small"
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              color: '#004cffff',
              transform: 'rotate(225deg)',
            }}
          />
        </Tooltip>
      )}
      <Box className={classes?.cardFooter}>
        <Box className={classes?.actionContainer}>
          {userName ? (
            <Tooltip title={userName} placement="top" arrow>
              <Avatar {...stringAvatar(userName)} className={classes?.avatar} />
            </Tooltip>
          ) : (
            <Tooltip title="Assign this task" placement="top" arrow>
              <Avatar sx={{ width: 20, height: 20 }} />
            </Tooltip>
          )}

          {task?.endDate ? (
            <Tooltip title="Deadline" placement="top" arrow>
              <Button
                size="small"
                color={isCompleted ? 'inherit' : endDateColor}
                onClick={handleEndDateBtnClick}
                className={classes?.deadlineBtn}
              >
                {endDateText}
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Add Deadline" placement="top" arrow>
              <IconButton>
                <CalendarToday className={classes?.calendarIcon} onClick={handleEndDateBtnClick} />
              </IconButton>
            </Tooltip>
          )}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            sx={{ opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                open
                value={endDate}
                onChange={handleDateChange}
                disablePast
                renderInput={(params) => <TextField {...params} sx={{ width: 0, height: 0 }} />}
              />
            </LocalizationProvider>
          </Popover>
        </Box>
        <Box className={classes?.actionContainer}>
          {userId === task?.userId && !isClient() && !isStakeholder() && (
            <>
              {runningTask?.id === task?.id ? (
                <Tooltip title="Pause timer" placement="top" arrow>
                  <IconButton onClick={handleStopTimer} color="error">
                    <PauseCircleOutlined className={classes?.actionIcons} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Run timer in this task" placement="top" arrow>
                  <IconButton onClick={handleStartTimer} color="success">
                    <PlayCircleOutline className={classes?.actionIcons} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default TaskCard;

const CheckCircleOutline = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
    />
  </svg>
);

const CheckCircleSolid = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
      clipRule="evenodd"
    />
  </svg>
);

TaskCard.propTypes = {
  task: PropTypes.object,
  setLoading: PropTypes.func,
  runningTask: PropTypes.object,
  refetchSection: PropTypes.func,
};
